import { createSignal, onMount, createEffect, For, createMemo } from 'solid-js';
import { STORAGE_KEY_PROFILE, DEFAULT_AVATAR, DEFAULT_USERNAME, MAX_USERNAME_LENGTH, avatarSrc } from '../../../game/constants';
import ModalBase from './ModalBase';
import GlowButton from '../ui/GlowButton';
import { showToast } from '../ui/ToastNotification';
import { gameStore } from '../../../store/gameStore';
import { isRealMode } from '../../../api';

const avatarGlob = import.meta.glob('/assets/avatars/*.png', { eager: true, query: '?url', import: 'default' });
const AVATARS: string[] = Object.keys(avatarGlob)
  .map(p => p.split('/').pop()!.replace('.png', ''))
  .sort();

interface Props { open: boolean; onClose: () => void; }

export default function ProfileSettingsModal(props: Props) {
  const [username, setUsername] = createSignal('');
  const [avatar, setAvatar] = createSignal(DEFAULT_AVATAR);

  const load = () => {
    if (isRealMode) {
      // In real mode, profile comes from session:init via gameStore
      const p = gameStore.profile();
      setUsername(p.username || '');
      setAvatar(p.avatar || DEFAULT_AVATAR);
      return;
    }
    try {
      const raw = localStorage.getItem(STORAGE_KEY_PROFILE);
      if (raw) {
        const json = JSON.parse(raw);
        setUsername(json.username || '');
        setAvatar(json.avatar || DEFAULT_AVATAR);
      } else { setUsername(''); setAvatar(DEFAULT_AVATAR); }
    } catch { setUsername(''); setAvatar(DEFAULT_AVATAR); }
  };

  onMount(load);
  createEffect(() => { if (props.open) load(); });

  const save = () => {
    const data = { username: username().trim(), avatar: avatar() };
    try { localStorage.setItem(STORAGE_KEY_PROFILE, JSON.stringify(data)); } catch {}
    // Sync to server via gameStore (sends profile:update WS message in real mode)
    gameStore.updateProfile(data);
    try { (window as any).memePumpApp?.updateProfileUI?.(data); } catch {}
    try { (window as any).memePumpApp?.audio?.play?.('ui-toggle', { allowRestart: true }); } catch {}
    showToast('success', 'Profile updated successfully');
  };

  const currentIndex = () => {
    const idx = AVATARS.indexOf(avatar());
    return idx >= 0 ? idx : 0;
  };

  const prev = () => {
    const idx = currentIndex();
    setAvatar(AVATARS[idx <= 0 ? AVATARS.length - 1 : idx - 1]);
    playClick();
  };
  const next = () => {
    const idx = currentIndex();
    setAvatar(AVATARS[idx >= AVATARS.length - 1 ? 0 : idx + 1]);
    playClick();
  };

  const playClick = () => { try { (window as any).memePumpApp?.audio?.play?.('ui-toggle', { allowRestart: true }); } catch {} };

  const count = AVATARS.length;
  const getOffset = (i: number) => {
    const sel = currentIndex();
    let offset = i - sel;
    if (offset > Math.floor(count / 2)) offset -= count;
    if (offset < -Math.floor(count / 2)) offset += count;
    return offset;
  };

  let startX = 0;
  let dragging = false;
  const onPointerDown = (e: PointerEvent) => { startX = e.clientX; dragging = true; };
  const onPointerUp = (e: PointerEvent) => {
    if (!dragging) return;
    dragging = false;
    const dx = e.clientX - startX;
    if (Math.abs(dx) > 30) { dx < 0 ? next() : prev(); }
  };

  return (
    <ModalBase id="profile-solid" title="Profile Settings" open={props.open} onClose={props.onClose} size="sm">
      <div class="mp-profile mp-profile-new">
        <div class="mp-profile-hero">
          <div class="mp-hero-avatar">
            <img
              src={avatarSrc(avatar())}
              alt="Avatar"
            />
            <div class="mp-hero-ring" />
            <div class="mp-hero-glow" />
          </div>
          <div class="mp-hero-name">{username() || DEFAULT_USERNAME}</div>
          <div class="mp-hero-sub">Your public screen name and avatar</div>
        </div>

        <div class="mp-field">
          <label class="mp-label">Username</label>
          <input
            class="mp-input"
            data-autofocus
            value={username()}
            maxlength={MAX_USERNAME_LENGTH}
            placeholder="Enter your name"
            onInput={(e) => setUsername(e.currentTarget.value)}
          />
        </div>

        <div class="mp-field">
          <label class="mp-label">Choose Avatar</label>
          <div
            class="mp-arc-carousel"
            onPointerDown={onPointerDown}
            onPointerUp={onPointerUp}
          >
            <button type="button" class="mp-arc-arrow mp-arc-prev" onClick={prev} aria-label="Previous avatar">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M15 6l-6 6 6 6"/></svg>
            </button>

            <div class="mp-arc-stage">
              <For each={AVATARS}>{(key, i) => {
                const offset = () => getOffset(i());
                const abs = () => Math.abs(offset());
                const isCenter = () => offset() === 0;
                const hidden = () => abs() > 3;

                return (
                  <button
                    type="button"
                    class="mp-arc-item"
                    classList={{ 'mp-arc-center': isCenter(), 'mp-arc-hidden': hidden() }}
                    style={{
                      '--arc-offset': String(offset()),
                      '--arc-abs': String(abs()),
                    }}
                    onClick={() => { if (!isCenter()) { setAvatar(key); playClick(); } }}
                    aria-label={`Avatar ${i() + 1}`}
                  >
                    <div class="mp-arc-img-wrap">
                      <img
                        src={avatarSrc(key)}
                        alt=""
                        draggable={false}
                      />
                    </div>
                  </button>
                );
              }}</For>
            </div>

            <button type="button" class="mp-arc-arrow mp-arc-next" onClick={next} aria-label="Next avatar">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M9 6l6 6-6 6"/></svg>
            </button>
          </div>
        </div>

        <div class="mp-modal-actions">
          <GlowButton class="mp-primary" successText="Saved" onClick={save} ariaLabel="Save profile">
            Save Changes
          </GlowButton>
        </div>
      </div>
    </ModalBase>
  );
}
